import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
import { useState, useEffect, useRef } from 'react';
import _chunk from 'lodash/chunk';
import _flatten from 'lodash/flatten';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _isNumber from 'lodash/isNumber';
import _range from 'lodash/range';
import { getItems } from './use-pagination.utils';
import { DEFAULT_AUTO_SCROLL_INTERVAL } from '../../Carousel/services/constants';
var defaultOptions = {
  defaultActivePage: 1,
  carousel: false,
  continuous: false,
  autoScroll: false,
  stepSize: null,
  slide: false,
  grouping: 1,
  autoScrollInterval: DEFAULT_AUTO_SCROLL_INTERVAL,
  infinite: false
};
export default function usePagination(items, pageSize) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var finalOptions = _objectSpread(_objectSpread({}, defaultOptions), options);
  var defaultActivePage = finalOptions.defaultActivePage,
    carousel = finalOptions.carousel,
    continuous = finalOptions.continuous,
    autoScroll = finalOptions.autoScroll,
    autoScrollInterval = finalOptions.autoScrollInterval,
    stepSize = finalOptions.stepSize,
    slide = finalOptions.slide,
    grouping = finalOptions.grouping,
    infinite = finalOptions.infinite;
  var isCustomStep = !_isNil(stepSize);
  var chunkSize = isCustomStep ? Math.min(stepSize, pageSize) : pageSize;
  var groupedItems = _chunk(items, grouping);
  var finalItems = getItems({
    items: groupedItems,
    slide: slide,
    pageSize: pageSize,
    continuous: continuous
  });
  var totalPages = _chunk(slide ? finalItems : groupedItems, chunkSize).length;
  var _useState = useState(defaultActivePage),
    _useState2 = _slicedToArray(_useState, 2),
    activePage = _useState2[0],
    setActivePage = _useState2[1];
  var isValidInterval = _isNumber(autoScrollInterval) && autoScrollInterval > 0;
  var _useState3 = useState(totalPages > 1 && isValidInterval ? autoScroll : false),
    _useState4 = _slicedToArray(_useState3, 2),
    autoScrollEnabled = _useState4[0],
    setAutoScrollEnabled = _useState4[1];
  var autoScrollTimer = useRef(null);
  function buildItem(item) {
    if (grouping !== 1 || isCustomStep) {
      return item;
    }
    return item.map(function (element) {
      return element[0];
    });
  }
  function getOriginalItems() {
    return items;
  }
  function getAllItems() {
    return _flatten(finalItems);
  }
  function getActivePageItems() {
    var pageChunks = _chunk(finalItems, chunkSize);
    var currentIndex = activePage - 1;
    if (currentIndex > totalPages || activePage < 1) {
      return [];
    }
    if (currentIndex > pageChunks.length - 1) {
      return [];
    }
    if (!isCustomStep || chunkSize === pageSize) {
      if (infinite) {
        return _flatten(_range(activePage).map(function (i) {
          return buildItem(pageChunks[i]);
        }));
      }
      return buildItem(pageChunks[currentIndex]);
    }
    var start = chunkSize * currentIndex;
    return _flatten(pageChunks).slice(start, start + pageSize);
  }
  function onPageChange(e, _ref) {
    var nextActivePage = _ref.activePage;
    setActivePage(nextActivePage);
  }
  function getPreviousPage(page) {
    if (carousel && page === 1) {
      return totalPages;
    }
    if (!carousel && page === 1) {
      return page;
    }
    return page - 1;
  }
  function onPreviousPage() {
    setActivePage(function (previousActivePage) {
      return getPreviousPage(previousActivePage);
    });
  }
  function getNextPage(page) {
    if (carousel && page >= totalPages) {
      return 1;
    }
    if (!carousel && page >= totalPages) {
      return page;
    }
    return page + 1;
  }
  function onNextPage() {
    setActivePage(function (previousActivePage) {
      return getNextPage(previousActivePage);
    });
  }
  function clearAutoScrollTimerInterval() {
    if (autoScrollTimer.current) {
      clearInterval(autoScrollTimer.current);
      autoScrollTimer.current = null;
    }
  }
  function startAutoScrollTimerInterval() {
    clearAutoScrollTimerInterval();
    if (autoScrollEnabled && isValidInterval) {
      autoScrollTimer.current = setInterval(onNextPage, autoScrollInterval);
    }
  }
  function onPreviousPageWithReset() {
    startAutoScrollTimerInterval();
    onPreviousPage();
  }
  function onNextPageWithReset() {
    startAutoScrollTimerInterval();
    onNextPage();
  }
  function onPageChangeWithReset() {
    startAutoScrollTimerInterval();
    onPageChange.apply(void 0, arguments);
  }
  function playAutoScroll() {
    if (isValidInterval) {
      setAutoScrollEnabled(true);
    }
  }
  function pauseAutoScroll() {
    setAutoScrollEnabled(false);
  }
  useEffect(function () {
    if (!_isEmpty(items)) {
      if (activePage > totalPages) {
        // this is to avoid index out of bounds situations
        setActivePage(totalPages);
      }
    }
  }, [items]);
  useEffect(function () {
    startAutoScrollTimerInterval();
    return function () {
      return clearAutoScrollTimerInterval();
    };
  }, [autoScrollEnabled, autoScrollInterval, totalPages]);
  return {
    activePage: activePage,
    autoScrollEnabled: autoScrollEnabled,
    onPageChange: onPageChangeWithReset,
    onPreviousPage: onPreviousPageWithReset,
    onNextPage: onNextPageWithReset,
    getOriginalItems: getOriginalItems,
    getAllItems: getAllItems,
    getActivePageItems: getActivePageItems,
    playAutoScroll: playAutoScroll,
    pauseAutoScroll: pauseAutoScroll,
    totalPages: totalPages,
    getPreviousPage: getPreviousPage,
    getNextPage: getNextPage,
    pageSize: pageSize,
    slide: slide
  };
}